import { Link } from '@tanstack/react-router';
import { Card } from '@orion/ui';
import { useTasksQuery, useUpdateTask } from './api.js';

interface OpenTasksCardProps {
  leadId?: string;
  contactId?: string;
  companyId?: string;
  opportunityId?: string;
  bare?: boolean;
}

/** A record detail page's "Open tasks" widget (Lead/Contact/Company/Deal —
 * design/orion-crm-design/rendered-html/LeadDetail.html) — real open tasks
 * for that record with a working complete-checkbox, not a static list. */
export function OpenTasksCard({ bare, ...filter }: OpenTasksCardProps) {
  const { data } = useTasksQuery({ ...filter, pageSize: 10 });
  const updateTask = useUpdateTask();
  const openTasks = (data?.data ?? []).filter((t) => t.status === 'open' || t.status === 'in_progress');

  const complete = (id: string, version: number) => {
    updateTask.mutate({ id, input: { status: 'completed', version } });
  };

  const body = (
    <>
      {!bare && (
        <div className="flex h-5 items-center justify-between">
          <h2 className="text-sm font-semibold text-ink">
            Open tasks <span className="font-medium text-text-muted">({openTasks.length})</span>
          </h2>
          <Link to="/tasks" className="text-xs font-semibold text-primary-text hover:underline">
            View all
          </Link>
        </div>
      )}
      {openTasks.length === 0 && <p className="text-sm text-text-muted">No open tasks.</p>}
      {openTasks.map((t, i) => (
        <label key={t.id} className={`flex items-start gap-2.5 py-1.5 text-[13px] leading-4.5 ${i > 0 ? 'border-t border-divider' : ''}`}>
          <input type="checkbox" className="mt-0.5 size-3.5 accent-primary" onChange={() => complete(t.id, t.version)} />
          <span className="flex flex-1 flex-col">
            <span className="font-semibold text-ink">{t.title}</span>
            <span className="text-xs text-text-muted">
              {t.dueAt ? `Due ${new Date(t.dueAt).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}` : 'No due date'}
              {t.owner ? ` · ${t.owner.displayName}` : ''}
            </span>
          </span>
        </label>
      ))}
    </>
  );

  if (bare) return <div className="flex flex-col gap-1">{body}</div>;
  return <Card className="flex shrink-0 flex-col gap-1.5 p-4">{body}</Card>;
}
